/**
 * Order payment instrument → shopper-facing label (KINDW-270.25).
 * Shared by order confirmation and GA4 `add_payment_info`.
 *
 * Stripe custom attributes (set on `OrderPaymentInstrument` by Phase 6's
 * `payment_intent.succeeded` webhook job):
 *
 *   - `c_stripePaymentMethodType` — `card | apple_pay | google_pay |
 *     afterpay_clearpay | zip | klarna | link | …`
 *   - `c_stripeBrand` — `visa | mastercard | amex | …` (cards only)
 *   - `c_stripeLast4` — card last-4 (cards only)
 *   - `c_stripeWallet` — `apple_pay | google_pay | link | ''`
 *
 * Webhook delivery is out-of-band, so callers must tolerate missing attrs at
 * first render and prefer `paymentCard.cardType` / `numberLastDigits` before
 * falling back to "Online payment".
 */

const STRIPE_PAYMENT_METHOD_ID = 'STRIPE_PAYMENT_ELEMENT';

const capitaliseStripeCardBrand = (brand) => {
  if (typeof brand !== 'string' || !brand) return brand;

  if (brand === 'amex') return 'American Express';

  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

const formatStripeMethod = (paymentInstrument, formatMessage, localeId) => {
  const type = paymentInstrument.c_stripePaymentMethodType;
  const brand = paymentInstrument.c_stripeBrand;
  const last4 = paymentInstrument.c_stripeLast4;
  const wallet = paymentInstrument.c_stripeWallet;

  const labelApplePay = formatMessage({
    defaultMessage: 'Apple Pay',
    id: 'order_confirmation.payment.apple_pay',
  });
  const labelGooglePay = formatMessage({
    defaultMessage: 'Google Pay',
    id: 'order_confirmation.payment.google_pay',
  });
  const labelLink = formatMessage({
    defaultMessage: 'Link',
    id: 'order_confirmation.payment.link',
  });
  const labelAfterpay = (localeId || '').toLowerCase().startsWith('en-gb') ?
    formatMessage({defaultMessage: 'Clearpay payment', id: 'order_confirmation.payment.clearpay'}) :
    formatMessage({defaultMessage: 'Afterpay payment', id: 'order_confirmation.payment.afterpay'});
  const labelZip = formatMessage({
    defaultMessage: 'Zip payment',
    id: 'order_confirmation.payment.zip',
  });
  const labelKlarna = formatMessage({
    defaultMessage: 'Klarna payment',
    id: 'order_confirmation.payment.klarna',
  });
  const labelOnline = formatMessage({
    defaultMessage: 'Online payment',
    id: 'order_confirmation.payment.online_fallback',
  });

  switch (type) {
    case 'card':
      if (wallet === 'apple_pay') return labelApplePay;
      if (wallet === 'google_pay') return labelGooglePay;
      if (wallet === 'link') return labelLink;
      if (brand && last4) {
        return formatMessage(
          {
            defaultMessage: '{brand} ending in {last4}',
            id: 'order_confirmation.payment.card_summary',
          },
          {brand: capitaliseStripeCardBrand(brand), last4}
        );
      }

      return labelOnline;
    case 'apple_pay':
      return labelApplePay;
    case 'google_pay':
      return labelGooglePay;
    case 'afterpay_clearpay':
      return labelAfterpay;
    case 'zip':
      return labelZip;
    case 'klarna':
      return labelKlarna;
    case 'link':
      return labelLink;
    default:
      return labelOnline;
  }
};

/**
 * @param {Object} paymentInstrument
 * @param {Function} formatMessage - react-intl `formatMessage`
 * @param {string} [localeId]
 * @returns {string|null}
 */
export const formatPaymentMethod = (paymentInstrument, formatMessage, localeId) => {
  if (!paymentInstrument) return null;

  if (paymentInstrument.paymentMethodId === STRIPE_PAYMENT_METHOD_ID) {
    if (paymentInstrument.c_stripePaymentMethodType) {
      return formatStripeMethod(paymentInstrument, formatMessage, localeId);
    }
  }

  const card = paymentInstrument.paymentCard;

  if (card?.cardType && card?.numberLastDigits) {
    return formatMessage(
      {
        defaultMessage: '{brand} ending in {last4}',
        id: 'order_confirmation.payment.card_summary',
      },
      {brand: capitaliseStripeCardBrand(card.cardType), last4: card.numberLastDigits}
    );
  }

  if (paymentInstrument.paymentMethodId === STRIPE_PAYMENT_METHOD_ID) {
    return formatMessage({
      defaultMessage: 'Online payment',
      id: 'order_confirmation.payment.online_fallback',
    });
  }

  if (paymentInstrument.paymentMethodId === 'PayPal') {
    return formatMessage({
      defaultMessage: 'PayPal / Pay Later',
      id: 'order_confirmation.payment.paypal',
    });
  }

  const provider = paymentInstrument.paymentMethod?.name ||
    paymentInstrument.paymentMethodId;

  if (!provider) return null;

  return provider.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Whether a Stripe order's payment method label has resolved (confirmation polling).
 * Order-level `c_paymentMethodLabel` is stamped by `/server/stamp-payment-method`
 * and the async webhook; instrument-level attrs are fallbacks.
 *
 * @param {Object} [order]
 * @returns {boolean}
 */
export const isStripeMethodResolved = (order) => {
  const opi = order?.paymentInstruments?.[0];

  if (!opi) return true;
  if (opi.paymentMethodId !== STRIPE_PAYMENT_METHOD_ID) return true;

  return (
    Boolean(order?.c_paymentMethodLabel) ||
    Boolean(opi.c_stripePaymentMethodType) ||
    Boolean(opi.paymentCard?.cardType && opi.paymentCard?.numberLastDigits)
  );
};

export {STRIPE_PAYMENT_METHOD_ID};
